/*
 * Copyright (c) 2026, Gather
 * https://gather.life
 */

'use client';

import { memo, type ReactNode, useCallback, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import Box from '@mui/material/Box/Box';

import Icon from '@marketplace/gather-ui/components/icon';
import Typography from '@marketplace/gather-ui/components/typography';
import { GatherLightSystemTokens } from '@marketplace/gather-ui/theme/colors';
import { AppSpacing, BorderRadius } from '@marketplace/gather-ui/theme/constants';

const suggestionStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: AppSpacing.SM,
  py: AppSpacing.SM,
  mx: `-${AppSpacing.SM}`,
  px: AppSpacing.SM,
  borderRadius: `${BorderRadius.Alert}px`,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: GatherLightSystemTokens.Surface.offset,
  },
  '&:focus': {
    backgroundColor: GatherLightSystemTokens.Surface.offset,
    outline: 'none',
  },
};

type SuggestionProps = {
  highlightMatch(suggestion: string): ReactNode;
  isMobile: boolean;
  selectSuggestion(suggestion: string): Promise<void>;
  suggestion: string;
};

export const Suggestion = memo(function Suggestion({
  highlightMatch,
  isMobile,
  selectSuggestion,
  suggestion,
}: SuggestionProps): ReactNode {
  const handleSuggestionClick = useCallback(
    () => selectSuggestion(suggestion),
    [selectSuggestion, suggestion]
  );

  const handleKeyPress = useCallback(
    (keyboardEvent: ReactKeyboardEvent) => {
      keyboardEvent.stopPropagation();
      if (keyboardEvent.key === 'Enter') {
        handleSuggestionClick();
      }
    },
    [handleSuggestionClick]
  );

  return (
    <Box
      component='li'
      role='option'
      sx={suggestionStyles}
      tabIndex={0}
      onClick={handleSuggestionClick}
      onKeyDown={handleKeyPress}
    >
      {/* Hide search icon on mobile */}
      {!isMobile && <Icon color='secondary' icon='search' />}
      <Typography variant='body'>{highlightMatch(suggestion)}</Typography>
    </Box>
  );
});
